import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Form } from '@unform/web';
import { FiSearch, FiVideo, FiVideoOff, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';

// Servicos
import api from 'services/api';

// Components
import LoadingTable from 'components/LoadingTable';
import Widget from 'components/Widget';
import Card from 'components/Card';
import Input from 'components/Input';
import Flag from 'components/Flag';
import Button from 'components/Button';
import Pagination from 'components/Pagination';

import { Container, Header } from './styles';
import { Scroll, Table } from 'styles/global';
import AppError from 'utils/AppError';
import { ComercialItem } from 'pages/Eleicoes/@types';

interface ListaExclusaoComercialItem {
  id: number;
  codmaterial: number;
  comercial: ComercialItem;
  gravado: boolean;
}

interface Filtro {
  search: string;
  status: string;
}

export default function ListaExclusaoId() {
  // react-router-dom
  const navigate = useNavigate();
  /**
   * USE STATE
   */
  const [loading, setLoading] = useState(true);
  const [exclusaoIds, setExclusaoIds] = useState<ListaExclusaoComercialItem[]>(
    [],
  );
  // const [exclusaoIdsCompleto, setExclusaoIdsCompleto] = useState([]);
  const [total, setTotal] = useState({ gravado: 0, naogravado: 0 });
  const [filtro, setFiltro] = useState<Filtro>({ search: '', status: '' });
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);

  const { emissora } = useSelector((state: any) => state.usuario);
  /**
   * USE PARAMS
   */
  const { listaId } = useParams<{ listaId: string }>();

  /**
   * USE EFFECT
   */
  useEffect(() => {
    const controller = new AbortController();
    async function loadIds() {
      try {
        const response = await api.get(
          `/emissoras/${emissora.value}/listasexclusao/${listaId}`,
          {
            signal: controller.signal,
            params: { page },
          },
        );
        if (response && response.data) {
          setExclusaoIds(response.data.listaexclusaoComercial);
          setFiltro({ search: '', status: '' });
          setTotal({
            gravado: response.data.totalgravado,
            naogravado: response.data.totalnaogravado,
          });
          setPages(response.data.pages);
        }
        setLoading(false);
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
        setLoading(false);
      }
    }
    setLoading(true);
    loadIds();
    return () => {
      controller.abort();
    };
  }, [emissora, listaId, page]);

  /**
   * FUNÇÕES
   */
  const filteredExclusaoIds = useMemo(() => {
    let filteredResult = exclusaoIds;
    if (filtro.status)
      filteredResult = exclusaoIds.filter(
        i => i.gravado === (filtro.status === 'gravado'),
      );
    if (filtro.search)
      filteredResult = filteredResult.filter(i =>
        i.comercial
          ? i.codmaterial.toString().includes(filtro.search) ||
            i.comercial.titulo
              .toUpperCase()
              .includes(filtro.search.toUpperCase())
          : i.codmaterial.toString().includes(filtro.search),
      );
    return filteredResult;
  }, [exclusaoIds, filtro]);

  return (
    <Container>
      <Header>
        <Button size="lg" color="primary" onClick={() => navigate(-1)}>
          VOLTAR
          <FiArrowLeft />
        </Button>
        <Widget
          title="Gravados"
          showLink={false}
          handleClick={() => setFiltro({ ...filtro, status: 'gravado' })}
          active={filtro.status === 'gravado'}
          icon={<FiVideo />}
          color="success"
          loading={loading}
          type="mini"
          data={total.gravado}
        />
        <Widget
          title="Não Gravados"
          showLink={false}
          handleClick={() => setFiltro({ ...filtro, status: 'naogravado' })}
          active={filtro.status === 'naogravado'}
          icon={<FiVideoOff />}
          color="danger"
          type="mini"
          loading={loading}
          data={total.naogravado}
        />
      </Header>
      <Card>
        <>
          <header>
            <h4>Lista de Exclusão</h4>
            <Pagination
              total={pages}
              activePage={page}
              onClick={page => setPage(page)}
            />
            <div>
              <Form onSubmit={() => {}}>
                <Input
                  name="searchLocal"
                  placeholder="Buscar na Tabela"
                  icon={<FiSearch />}
                  onChange={e =>
                    setFiltro({
                      ...filtro,
                      search: e.target.value.toUpperCase(),
                    })
                  }
                />
              </Form>
            </div>
          </header>
          {filteredExclusaoIds.length > 0 && !loading && (
            <Scroll>
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>CM</th>
                    <th>ÚLTIMO TITULO</th>
                    <th style={{ width: 180 }}>STATUS</th>
                  </tr>
                </thead>

                <tbody>
                  {!!filteredExclusaoIds &&
                    filteredExclusaoIds.map(i => (
                      <tr key={i.id}>
                        <td>{i.codmaterial}</td>
                        <td>{i.comercial ? i.comercial.titulo : `-`}</td>
                        <td>
                          <Flag
                            name={i.gravado ? 'GRAVADO' : 'NÃO GRAVADO'}
                            type={i.gravado ? 'gravado' : 'naogravado'}
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Scroll>
          )}
        </>
        {exclusaoIds.length <= 0 && !loading && <p>Nenhuma linha encontrada</p>}
        {loading && <LoadingTable type="default" />}
      </Card>
    </Container>
  );
}
